import { NavLink } from 'react-router-dom';
import { OrganizationSwitcher } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/lib/icons';
import { IqName } from '@/components/IqName';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { useClientAccess } from '@/auth/useClientAccess';
import { getReferrals, getStudioStatus } from '@/data/client';
import { AccountSwitcher } from './AccountSwitcher';
import { NAV } from './nav';

/* The sidebar is a static rail on desktop and an off-canvas drawer on mobile.
   `open` toggles the drawer; `onClose` fires from the scrim, the close button,
   and (via AppShell) on navigation. */
export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const isAdmin = useIsAdmin();
  const { access } = useClientAccess();
  const { data: studio } = useQuery({ queryKey: ['studioStatus'], queryFn: getStudioStatus, staleTime: 5 * 60_000 });
  const { data: referrals } = useQuery({ queryKey: ['referrals'], queryFn: getReferrals, staleTime: 10 * 60_000 });
  // Report nav needs canReports; contentIQ needs canContent. Expert Studio (below)
  // is shown to every role when the client has it enabled. Default to showing
  // while access loads to avoid a flash.
  const canReports = access ? access.canReports : true;
  const canContent = access ? access.canContent : true;
  const navItems = NAV.filter((n) => (n.path === '/content' ? canContent : canReports));

  return (
    <>
      <div
        className={`dash-scrim ${open ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`dash-side ${open ? 'is-open' : ''}`}>
        <div className="dash-side__logo">
          <img src="/triton-logo.png" alt="Triton" className="dash-side__logoimg" />
          <button className="dash-side__close" type="button" onClick={onClose} aria-label="Close menu">
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Client (organization) switcher. Admins get a searchable picker (they
            belong to every client); clients keep Clerk's switcher (1–2 orgs). */}
        <div className={isAdmin ? 'dash-side__acct' : 'dash-side__client dash-side__client--clerk'}>
          {isAdmin ? (
            <AccountSwitcher />
          ) : (
            <OrganizationSwitcher
              hidePersonal
              appearance={{
                elements: {
                  rootBox: { width: '100%' },
                  organizationSwitcherTrigger: { width: '100%', justifyContent: 'space-between' },
                  // Clients must not create organizations — hide the create action.
                  organizationSwitcherPopoverActionButton__createOrganization: { display: 'none' },
                },
              }}
            />
          )}
        </div>

        <nav className="dash-side__nav">
          {navItems.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.end}
              className={({ isActive }) => `dash-navitem ${isActive ? 'is-active' : ''}`}
            >
              <Icon name={n.icon} size={18} />
              <IqName text={n.label} />
              {n.count != null && <span className="dash-navitem__count">{n.count}</span>}
            </NavLink>
          ))}

          {studio?.enabled && (
            <NavLink to="/studio" className={({ isActive }) => `dash-navitem ${isActive ? 'is-active' : ''}`}>
              <Icon name="image" size={18} />
              <span>Expert Studio</span>
            </NavLink>
          )}

          {access?.canAssistant && (
            <NavLink to="/assistant" className={({ isActive }) => `dash-navitem ${isActive ? 'is-active' : ''}`}>
              <Icon name="sparkles" size={18} />
              <span>Ask tritonIQ</span>
            </NavLink>
          )}

          {/* Referral Rewards: every login, kept quiet so the reports stay the point. */}
          <NavLink to="/referrals" className={({ isActive }) => `dash-navitem dash-navitem--quiet ${isActive ? 'is-active' : ''}`}>
            <Icon name="heart" size={18} />
            <span>Refer a business<small>earn ${referrals?.rate ?? 100}</small></span>
          </NavLink>

          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `dash-navitem ${isActive ? 'is-active' : ''}`}
            >
              <Icon name="shield-check" size={18} />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
      </aside>
    </>
  );
}
